import React from "react";
import { 
  Box, 
  TextField, 
  IconButton, 
  Chip,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  FormHelperText,
  Autocomplete,
  CircularProgress,
  Typography,
} from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useForm, Controller } from 'react-hook-form';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { TextInputField } from '../formField/TextInputField'; // Import your component
import { getCompanyList, getUserList, updatePage, updatePerPage, createUser, updateUser, deleteUser, updateUserStatus } from "../../redux/actions/userList";
import { useDispatch, useSelector } from "react-redux";
import { useDebunce } from "../common/Debunce";
import LoadingTable from "../common/tableLoading";

interface Column {
  id: 'no' | 'full_name' | 'fullName' | 'companyRole' | 'phoneNumber' | 'email' | 'type' | 'roles' | 'phone_number' | 'status' | 'company_name' | 'actions';
  label: string;
  minWidth?: number;
  align?: 'right' | 'left' | 'center';
  format?: (value: number) => string;
}

// Form interface
interface UserFormData {
  full_name: string;
  email: string;
  roles: string;
  phone_number: string;
  company_id: string;
  company: CompanyList | null;
}

// User data interface
interface UserData {
  id?: number | string;
  full_name?: string;
  fullName: string;
  email: string | null;
  roles: string;
  phone_number: string | null;
  status?: boolean;
}
interface CompanyList {
  id: string;
  name: string;
}

// Available options
const roleOptions = [ 'EMPLOYER_JOB_ADMIN', 'EMPLOYER_USER'];

// Select field styling
const selectFieldStyle = {
  boxShadow: "0px 0px 11px 2px #4B44D41A inset",
  borderRadius: "95px",
  "& .MuiOutlinedInput-root": {
    borderRadius: "95px",
    "& fieldset": {
      borderColor: "#E0E0E0",
    },
    "&:hover fieldset": {
      borderColor: "#756DE9",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#756DE9",
    },
  },
  "& .MuiSelect-select": {
    padding: "12px 20px",
    color: "#333333",
  },
};

// Multi-select field styling
const multiSelectFieldStyle = {
  boxShadow: "0px 0px 11px 2px #4B44D41A inset",
  borderRadius: "22px !important",
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    "& fieldset": {
      borderColor: "#E0E0E0",
    },
    "&:hover fieldset": {
      borderColor: "#756DE9",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#756DE9",
    },
  },
  "& .MuiSelect-select": {
    padding: "12px 14px",
    color: "#333333",
  },
};

const labelStylestextfield = {
  fontSize: "18px",
  fontWeight: 500,
  color: "#333333",
  paddingLeft: "14px",
};

const columns: readonly Column[] = [
  { id: 'no', label: 'No.', minWidth: 50, align: 'center' },
  { id: 'fullName', label: 'Full Name', minWidth: 170 },
  { id: 'email', label: 'Email', minWidth: 200 },
  { id: 'company_name', label: 'Company', minWidth: 150 },
  { id: 'companyRole', label: 'Roles', minWidth: 150 },
  { id: 'phoneNumber', label: 'Phone', minWidth: 120 },
  { id: 'status', label: 'Status', minWidth: 100, align: 'center' },
  { id: 'actions', label: 'Actions', minWidth: 120, align: 'center' }
];


const UserList = () => {
  const [searchText, setSearchText] = React.useState("");
  const [selectCompany, setSelectCompany] = React.useState<CompanyList | null>(null);
  const [openModal, setOpenModal] = React.useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = React.useState(false);
  const [openActivateDialog, setOpenActivateDialog] = React.useState(false);
  const [editingUser, setEditingUser] = React.useState<UserData | null>(null);
  const [userToDelete, setUserToDelete] = React.useState<UserData | null>(null);
  const [userToActivate, setUserToActivate] = React.useState<UserData | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isActivating, setIsActivating] = React.useState(false);
  const dispatch = useDispatch();

  const { list, totalCount, rowsPerPage, page, companyList } = useSelector((state: any) => state.userList);
  // const { user } = useSelector((state: any) => state.app); 
  // const userdata = user?.user;
  
  // React Hook Form setup
  const { control, handleSubmit, reset, setValue, formState: { } } = useForm<UserFormData>({
    defaultValues: {
      full_name: '',
      email: '',
      roles: '',
      phone_number: '',
      company_id: selectCompany?.id || '',
      company: selectCompany || null
    }
  });

  const handleChangePage = (event: unknown, newPage: number) => {
    dispatch(updatePage(newPage + 1));
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(updatePerPage(event.target.value));
  };

  // Status chip styling function
  const getStatusChip = (status: 'active' | 'inactive' | 'pending' | true | false) => {
    const statusConfig = {
      active: {
        color: '#4CAF50',
        backgroundColor: '#E8F5E8',
        label: 'Active'
      },
      inactive: {
        color: '#F44336',
        backgroundColor: '#FFEBEE',
        label: 'Inactive'
      },
      pending: {
        color: '#FF9800',
        backgroundColor: '#FFF3E0',
        label: 'Pending'
      },
      true: {
        color: '#4CAF50',
        backgroundColor: '#E8F5E8',
        label: 'Active'
      },
      false: {
        color: '#F44336',
        backgroundColor: '#FFEBEE',
        label: 'Inactive'
      },
    };

    const config = statusConfig[String(status) as keyof typeof statusConfig];
    
    return (
      <Chip
        label={config.label}
        size="small"
        sx={{
          backgroundColor: config.backgroundColor,
          color: config.color,
          fontWeight: 500,
          border: `1px solid ${config.color}20`,
          '&:hover': {
            backgroundColor: config.backgroundColor,
          }
        }}
      />
    );
  };

  // Handle create/update user form submission
  const onSubmit = async (data: UserFormData) => {
    setIsSubmitting(true);
    try {
      // Check for selected company id before proceeding
      if (!selectCompany?.id && !data.company_id) {
        setIsSubmitting(false);
        throw new Error("No company selected. Please select a company before creating or editing a user.");
      }

      if (editingUser) {
        // Update existing user
        const editUser: any = {
          fullName: data.full_name,
          email: data.email,
          companyRole: data.roles,
          phoneNumber: data.phone_number,
          cid: parseInt(selectCompany?.id || data.company_id || ''),
          id: editingUser?.id ?? undefined,
        };

        const res = await dispatch(updateUser(editUser));
        
        // If user was inactive and we're editing them, activate them
        if (res && !editingUser.status && editingUser.id) {
          await dispatch(updateUserStatus(parseInt(editingUser.id.toString()), parseInt(selectCompany?.id || data.company_id || ''), "ACTIVE"));
        }
        
        if (res) {
          dispatch(getUserList({ searchText: searchTextFetch, cid: parseInt(selectCompany?.id || data.company_id || '') }));
        } 
      } else {
        // Create new user
        const newUser = {
          fullName: data.full_name,
          email: data.email,
          companyRole: data.roles,
          phoneNumber: data.phone_number,
          cid: data.company_id ? parseInt(data.company_id) : undefined,
        };

        const res = await dispatch(createUser(newUser));
        if (data.company_id && res) {
          dispatch(getUserList({ searchText: searchTextFetch, cid: parseInt(data.company_id) }));
        }
      }
      
      // Close modal and reset form
      setOpenModal(false);
      setEditingUser(null);
      reset({
        full_name: '',
        email: '',
        roles: '',
        phone_number: '',
        company_id: selectCompany?.id || '',
        company: selectCompany || null
      });
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle modal close
  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingUser(null);
    setIsSubmitting(false);
    reset({
      full_name: '',
      email: '',
      roles: '',
      phone_number: '',
      company_id: selectCompany?.id || '',
      company: selectCompany || null
    });
  };

  // Handle create new user action
  const handleCreateUser = () => {
    setEditingUser(null);
    setIsSubmitting(false);
    // Set form values with currently selected company
    setValue('full_name', '');
    setValue('email', '');
    setValue('phone_number', '');
    setValue('roles', '');
    setValue('company_id', selectCompany?.id || '');
    setValue('company', selectCompany || null);
    
    setOpenModal(true);
  };

  // Handle edit action
  const handleEdit = (rowData: any) => {
    setEditingUser(rowData);
    setIsSubmitting(false);
    // Populate form with existing data
    setValue('full_name', rowData.fullName || '');
    setValue('email', rowData.email || '');
    setValue('phone_number', rowData.phoneNumber || '');
    setValue('roles', rowData.companyRole ? rowData.companyRole : '');
    setValue('company_id', selectCompany?.id || '');
    setValue('company', selectCompany || null);
    
    setOpenModal(true);
  };

  // Handle delete action - open confirmation dialog
  const handleDelete = (rowData: UserData) => {
    setUserToDelete(rowData);
    setOpenDeleteDialog(true);
  };

  // Confirm delete action
  const confirmDelete = async () => {
    if (userToDelete) {

      const deletedStatus = await dispatch(deleteUser(userToDelete.id, selectCompany?.id?parseInt(selectCompany.id):0));
      if(selectCompany?.id && deletedStatus){
        dispatch(getUserList({searchText: searchTextFetch, cid: parseInt(selectCompany.id)}));
      }
    }
    setOpenDeleteDialog(false);
    setUserToDelete(null);
  };

  // Cancel delete action
  const cancelDelete = () => {
    setOpenDeleteDialog(false);
    setUserToDelete(null);
  };

  // Handle activate action - open confirmation dialog
  const handleActivate = (rowData: UserData) => {
    setUserToActivate(rowData);
    setOpenActivateDialog(true);
  };

  // Confirm activate action
  const confirmActivate = async () => {
    if (userToActivate && userToActivate.id && selectCompany?.id) {
      setIsActivating(true);
      const activatedStatus = await dispatch(updateUserStatus(parseInt(userToActivate.id.toString()), parseInt(selectCompany.id), "ACTIVE"));
      if (activatedStatus) {
        dispatch(getUserList({searchText: searchTextFetch, cid: parseInt(selectCompany.id)}));
      }
      setIsActivating(false);
    }
    setOpenActivateDialog(false);
    setUserToActivate(null);
  };

  // Cancel activate action
  const cancelActivate = () => {
    setOpenActivateDialog(false);
    setUserToActivate(null);
  };
  
  const searchTextFetch = useDebunce(searchText, 300);
  const [isLoading, setIsLoading] = React.useState(false);

  // getUserList action call
  React.useEffect(() => {
    if (selectCompany?.id) {
      setIsLoading(true); // ✅ start loading

      dispatch(
        getUserList({
          searchText: searchTextFetch,
          cid: parseInt(selectCompany.id),
        })
      );

      setTimeout(() => {
      setIsLoading(false);
        
      }, 2000);
    }
  }, [rowsPerPage, page, searchTextFetch, selectCompany]);

  // Update form when selected company changes
  React.useEffect(() => {
    if (selectCompany) {
      setValue('company_id', selectCompany.id);
      setValue('company', selectCompany);
    }
  }, [selectCompany, setValue]);

  React.useEffect(() => {

    const init = () => {
      dispatch(getCompanyList());
      // if (isMounted && Array.isArray(dataList) && dataList.length > 0) {
      //   if(userdata?.companyId){
      //     setSelectCompany({ id: userdata?.companyId, name: userdata?.companyName });
      //   }else{
      //     setSelectCompany(dataList[0]);
      //   }
      // }
    };

    init();

    // if(userdata?.roles?.some((role: any) => allowedRoles.includes(role)) && userdata?.companyId){
    //   if(userdata?.roles?.includes('employer') &&  userdata?.companyRole === 'user_admin'){
    //      setSelectCompany({ id: userdata?.companyId, name: userdata?.companyName });
    //   }else if(userdata?.roles?.includes('admin')){
    //     init();
    //   } else{ 
    //      setSelectCompany({ id: userdata?.companyId, name: userdata?.companyName });
    //   }
    // }

  }, [dispatch]);

  const roleName = (role: string) => {
    switch(role) {
      case 'user_admin':
        return 'User Admin';
      case 'EMPLOYER_JOB_ADMIN':
        return 'Jobs Admin';
      case 'EMPLOYER_USER':
        return 'User';
      case 'user_reviewer':
        return 'User Reviewer';
      case 'employer':
        return 'Employer';
      case 'seevi_admin'.toUpperCase():
        return 'Reviewer';
      case 'admin':
        return 'Admin';
      case 'EMPLOYER_JOB_ADMIN':
        return 'Job Admin';
      case 'EMPLOYER_USER':
        return 'Job user';
      default:
        return role;
    }
  };


  return (
    <Box
      sx={{
        backgroundColor: "#ffffff",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >

      <Box
        sx={{
          padding: "26px",
          width: "100%",
        }}
      >
        {/* Header with Search and Create Button */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '20px',
          paddingX: '32px'
        }}>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: "659px",
              height: "45px",
              // border: "1px solid #756DE9",
              // borderRadius: "100px",
              position: "relative",
            }}
          >
           
             {/* company select list */}
            {/* {userdata?.roles?.includes('employer') &&  userdata?.companyRole === 'user_admin' ? */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "350px",
                height: "45px",
                border: "1px solid #756DE9",
                borderRadius: "100px",
                position: "relative",
                marginLeft: "20px"
              }}
            >
              <Autocomplete
                key={"selectcomplany"}
                options={companyList || []}
                getOptionLabel={(option) => option?.name || ''}
                value={selectCompany}
                onChange={(event, newValue) => {
                  setSelectCompany(newValue);
                }}
                onInputChange={(event, newInputValue) => {
                  // This enables the search functionality
                }}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                filterOptions={(options, { inputValue }) => {
                  if (!inputValue || inputValue.trim() === '') return options;
                  const normalizedInput = inputValue.trim().toLowerCase();
                  return options.filter((option) => {
                    const name = (option?.name || '').toString().toLowerCase();
                    console.log(name.includes(normalizedInput));
                    return name.includes(normalizedInput);
                  });
                }}
                freeSolo={false}
                autoComplete={false}
                autoHighlight={false}
                autoSelect={false}
                disableCloseOnSelect={false}
                clearOnBlur={false}
                selectOnFocus={false}
                sx={{
                  width: '320px',
                  ...selectFieldStyle
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select company"
                    sx={{
                     // boxShadow: "0px 0px 2px 2px #4B44D41A inset",
                      borderRadius: "95px",
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "95px",
                        "& fieldset": {
                          borderColor: "transparent",
                        },
                        "&:hover fieldset": {
                          borderColor: "transparent",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "transparent",
                        },
                      },
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    {option.name}
                  </Box>
                )}
                noOptionsText="No companies found"
                clearOnEscape
                handleHomeEndKeys
                openOnFocus
                ListboxProps={{
                  style: {
                    maxHeight: 350,
                    overflowY: "auto",
                  },
                }}
              />
        
            </Box>
            {/* : null } */}
          
            {/* Search Box */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "359px",
                height: "45px",
                border: "1px solid #756DE9",
                borderRadius: "100px",
                marginLeft: "20px",
                position: "relative",
              }}
            >
              <TextField
                variant="standard"
                placeholder="Search users..."
                sx={{
                  width: "100%",
                  paddingLeft: "15px",
                  fontSize: "16px",
                  "& .MuiInput-underline:before": { borderBottom: "none" },
                  "& .MuiInput-underline:after": { borderBottom: "none" },
                }}
                InputProps={{
                  disableUnderline: true,
                }}
                onChange={(e) => setSearchText(e.target.value)}
                value={searchText}
              />

              <IconButton
                sx={{
                  position: "absolute",
                  right: "10px",
                  padding: "0",
                }}
              >
                <Box
                  component="img"
                  src="/images/client-website/search-line.png"
                  alt="search icon"
                  sx={{
                    width: "24px",
                    height: "24px",
                  }}
                />
              </IconButton>
            </Box>

          </Box>
          

          {/* Create New User Button */}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateUser}
            sx={{
              backgroundColor: '#756DE9',
              color: 'white',
              borderRadius: '24px',
              textTransform: 'none',
              fontWeight: 600,
              paddingX: '20px',
              paddingY: '10px',
              '&:hover': {
                backgroundColor: '#5f5bc4',
              }
            }}
          >
            Create New User
          </Button>
        </Box>

        {/* Table Container */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", padding: "0px 30px" }}>
          <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: 4 }} elevation={3}>
            {/* {JSON.stringify(list)} */}
            <TableContainer sx={{ maxHeight: 560, minHeight: 560 }}>
              <Table stickyHeader aria-label="sticky table">
                <TableHead style={{ backgroundColor: "#756eea" }}>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        align={column.align || 'left'}
                        style={{ 
                          minWidth: column.minWidth, 
                          backgroundColor: "#756eea", 
                          color: "#fff", 
                          fontWeight: "600" 
                        }}
                      >
                        {column.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading && (
                    <LoadingTable rowsCount={9} columns={9} />
                  )}
                  {(!isLoading && list.length === 0) && (
                    <p style={{ position: "absolute", left: "50%", transform: "translateX(-50%)", marginTop: "50px", color: "#777777" }}>
                      {"No Users found."}
                    </p>
                  )}
                  {!isLoading && list.map((row: any, index: any) => {
                      return (
                        <TableRow hover role="checkbox" tabIndex={-1} key={row.email || index}>
                          {columns.map((column) => {
                            if(column.id === 'no'){
                              return (
                                <TableCell key={column.id} align={column.align || 'left'}>
                                  {(page - 1) * rowsPerPage + list.indexOf(row) + 1}
                                </TableCell>
                              );
                            }

                            if (column.id === 'status') {
                              return (
                                <TableCell key={column.id} align={column.align || 'left'}>
                                  {getStatusChip(row?.status)}
                                </TableCell>
                              );
                            }
                            if(column.id === 'companyRole'){
                              const getRoleDisplayName = (role: string) => {
                                switch(role) {
                                  case 'EMPLOYER_USER':
                                    return 'User';
                                  case 'EMPLOYER_JOB_ADMIN':
                                    return 'Job Admin';
                                  default:
                                    return role?.replaceAll("_", " ");
                                }
                              };
                              
                              
                              return(
                                <TableCell key={column.id} align={column.align || 'left'}>
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      color: '#333',
                                      fontWeight: '500'
                                    }}
                                  >
                                    {getRoleDisplayName(row?.companyRole) || '-'}
                                  </Typography>
                                </TableCell>
                              );
                            }

                            if(column.id === 'company_name'){
                              return(
                                <TableCell key={column.id} align={column.align || 'left'}>
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      color: '#333',
                                      fontWeight: '500'
                                    }}
                                  >
                                    {selectCompany?.name || row?.companyName || row?.company_name || '-'}
                                  </Typography>
                                </TableCell>
                              );
                            }
                            
                            if (column.id === 'actions') {
                            
                            
                              return (
                                <TableCell key={column.id} align={column.align || 'left'}>
                                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                    <Tooltip title="Edit User">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleEdit(row)}
                                        sx={{
                                          color: '#756DE9',
                                          '&:hover': {
                                            backgroundColor: '#756DE920',
                                          }
                                        }}
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    {row.status && (
                                      <Tooltip title="Delete User">
                                        <IconButton
                                          size="small"
                                          onClick={() => handleDelete(row)}
                                          sx={{
                                            color: '#F44336',
                                            '&:hover': {
                                              backgroundColor: '#F4433620',
                                            }
                                          }}
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </Box>
                                </TableCell>
                              );
                            }

                            if (column.id === 'email') {
                              return (
                                <TableCell key={column.id} align={column.align || 'left'}>
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      color: '#1976D2',
                                      textDecoration: 'none',
                                      '&:hover': {
                                        textDecoration: 'underline'
                                      }
                                    }}
                                  >
                                    {row.email || row.emailAddress || '-'}
                                  </Typography>
                                </TableCell>
                              );
                            }

                            const value = row[column.id as keyof typeof row];
                            return (
                              <TableCell key={column.id} align={column.align || 'left'}>
                                {column.format && typeof value === 'number'
                                  ? column.format(value)
                                  : value ?? "-"}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 100]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page-1}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        </Box>

        {/* Create/Edit User Modal */}
        <Dialog 
          open={openModal} 
          onClose={handleCloseModal}
          maxWidth="sm"
          fullWidth
          sx={{ '& .MuiDialog-paper': { borderRadius: 4 } }}
        >
          <DialogTitle sx={{ 
            backgroundColor: '#756DE9', 
            color: 'white', 
            fontWeight: 600,
            fontSize: '1.2rem'
          }}>
            {editingUser ? 'Edit User' : 'Create New User'}
          </DialogTitle>
          
          <form onSubmit={handleSubmit(onSubmit)}>
            <DialogContent sx={{ paddingTop: '20px !important' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column'}}>
                
                {/* User Name Field using Custom Component */}
                <TextInputField
                  name="full_name"
                  label="User Name"
                  control={control}
                  placeholder="Enter full name"
                  rules={{ required: 'User name is required' }}
                />

                {/* Email Field using Custom Component */}
                <TextInputField
                  name="email"
                  label="Email"
                  control={control}
                  placeholder="Enter email address"
                  type="email"
                  rules={{ 
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address'
                    }
                  }}
                />

                {/* Phone Field using Custom Component */}
                <TextInputField
                  name="phone_number"
                  label="Phone"
                  control={control}
                  placeholder="Enter phone number"
                  rules={{ required: 'Phone number is required' }}
                />

                {/* Company Selection Autocomplete */}
                <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
                  <InputLabel shrink sx={labelStylestextfield}>
                    Company
                  </InputLabel>
                  <Controller
                    name="company"
                    control={control}
                    rules={{ required: 'Company selection is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <Autocomplete
                          {...field}
                          options={companyList}
                          getOptionLabel={(option) => option?.name || ''}
                          value={field.value}
                          onChange={(event, newValue) => {
                            field.onChange(newValue);
                            // Also update the company_id field for form submission
                            setValue('company_id', newValue ? newValue.id : '');
                          }}
                          isOptionEqualToValue={(option, value) => option?.id === value?.id}
                          freeSolo={false}
                          autoComplete={false}
                          autoHighlight={true}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Search and select company"
                              error={!!error}
                              sx={{
                                boxShadow: "0px 0px 11px 2px #4B44D41A inset",
                                borderRadius: "95px",
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "95px",
                                  "& fieldset": {
                                    borderColor: "transparent",
                                  },
                                  "&:hover fieldset": {
                                    borderColor: "transparent",
                                  },
                                  "&.Mui-focused fieldset": {
                                    borderColor: "transparent",
                                  },
                                },
                              }}
                            />
                          )}
                          renderOption={(props, option) => (
                            <Box component="li" {...props}>
                              {option.name}
                            </Box>
                          )}
                          noOptionsText="No companies found"
                          clearOnEscape
                          selectOnFocus
                          handleHomeEndKeys
                          openOnFocus
                        />
                        <FormHelperText sx={{ color: "#d32f2f", ml: "14px", mt: "3px" }}>
                          {error ? error.message : " "}
                        </FormHelperText>
                      </>
                    )}
                  />
                </Box>

                {/* Roles Multi-Select - Custom Multi-Select */}
                <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
                  <InputLabel shrink sx={labelStylestextfield}>
                    Roles
                  </InputLabel>
                  <Controller
                    name="roles"
                    control={control}
                    rules={{ required: 'At least one role is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <FormControl fullWidth error={!!error}>
                          <Select
                            {...field}
                            
                            displayEmpty
                            input={<OutlinedInput />}
                            renderValue={(selected) => {
                              if (!selected) {
                                return <span style={{ color: '#777777' }}>Select roles</span>;
                              }
                              return roleName(selected as string);
                            }}
                            sx={multiSelectFieldStyle}
                          >
                            {roleOptions.map((role) => (
                              <MenuItem key={role} value={role}>
                                <Checkbox checked={field.value === role} />
                                <ListItemText primary={roleName(role)} />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <FormHelperText sx={{ color: "#d32f2f", ml: "14px", mt: "3px" }}>
                          {error ? error.message : " "}
                        </FormHelperText>
                      </>
                    )}
                  />
                </Box>

              </Box>
            </DialogContent>
            
            <DialogActions sx={{ padding: '20px 24px' }}>
              <Button 
                onClick={handleCloseModal}
                variant="outlined"
                sx={{ 
                  color: '#666',
                  borderColor: '#ddd',
                  textTransform: 'none',
                  fontWeight: 500
                }}
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                variant="contained"
                disabled={isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={16} sx={{ color: 'white' }} /> : null}
                sx={{ 
                  backgroundColor: '#756DE9',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: '#5f5bc4',
                  },
                  '&:disabled': {
                    backgroundColor: '#ccc',
                    color: '#666'
                  }
                }}
              >
                {isSubmitting ? (editingUser ? 'Updating...' : 'Creating...') : (editingUser ? 'Update' : 'Create')}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={openDeleteDialog}
          onClose={cancelDelete}
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
          maxWidth="sm"
          fullWidth
          sx={{ '& .MuiDialog-paper': { borderRadius: 4 } }}
        >
          <DialogTitle 
            id="delete-dialog-title" 
            sx={{ 
              backgroundColor: '#F44336', 
              color: 'white', 
              fontWeight: 600,
              fontSize: '1.2rem',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <DeleteIcon />
            Confirm Delete
          </DialogTitle>
          <DialogContent sx={{ paddingTop: '20px !important' }}>
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Box sx={{ fontSize: '1.1rem', mb: 1 }}>
                Are you sure you want to delete this user?
              </Box>
              {userToDelete && (
                <Box sx={{ 
                  fontSize: '1rem', 
                  fontWeight: 600, 
                  color: '#333',
                  backgroundColor: '#f5f5f5',
                  padding: '12px',
                  borderRadius: '8px',
                  mt: 2
                }}>
                  {userToDelete.fullName}
                  <br />
                  <span style={{ fontWeight: 400, fontSize: '0.9rem', color: '#666' }}>
                    {userToDelete.email}
                  </span>
                </Box>
              )}
              <Box sx={{ fontSize: '0.9rem', color: '#666', mt: 2 }}>
                This action cannot be undone.
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ padding: '20px 24px', justifyContent: 'center', gap: 2 }}>
            <Button 
              onClick={cancelDelete}
              variant="outlined"
              sx={{ 
                color: '#666',
                borderColor: '#ddd',
                textTransform: 'none',
                fontWeight: 500,
                minWidth: '100px'
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={confirmDelete}
              variant="contained"
              sx={{ 
                backgroundColor: '#F44336',
                textTransform: 'none',
                fontWeight: 600,
                minWidth: '100px',
                '&:hover': {
                  backgroundColor: '#d32f2f',
                }
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Activate Confirmation Dialog */}
        <Dialog
          open={openActivateDialog}
          onClose={cancelActivate}
          aria-labelledby="activate-dialog-title"
          aria-describedby="activate-dialog-description"
          maxWidth="sm"
          fullWidth
          sx={{ '& .MuiDialog-paper': { borderRadius: 4 } }}
        >
          <DialogTitle 
            id="activate-dialog-title" 
            sx={{ 
              backgroundColor: '#4CAF50', 
              color: 'white', 
              fontWeight: 600,
              fontSize: '1.2rem',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <CheckCircleIcon />
            Confirm Activation
          </DialogTitle>
          <DialogContent sx={{ paddingTop: '20px !important' }}>
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Box sx={{ fontSize: '1.1rem', mb: 1 }}>
                Are you sure you want to activate this user?
              </Box>
              {userToActivate && (
                <Box sx={{ 
                  fontSize: '1rem', 
                  fontWeight: 600, 
                  color: '#333',
                  backgroundColor: '#f5f5f5',
                  padding: '12px',
                  borderRadius: '8px',
                  mt: 2
                }}>
                  {userToActivate.fullName}
                  <br />
                  <span style={{ fontWeight: 400, fontSize: '0.9rem', color: '#666' }}>
                    {userToActivate.email}
                  </span>
                </Box>
              )}
              <Box sx={{ fontSize: '0.9rem', color: '#666', mt: 2 }}>
                The user will be able to access the system after activation.
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ padding: '20px 24px', justifyContent: 'center', gap: 2 }}>
            <Button 
              onClick={cancelActivate}
              variant="outlined"
              disabled={isActivating}
              sx={{ 
                color: '#666',
                borderColor: '#ddd',
                textTransform: 'none',
                fontWeight: 500,
                minWidth: '100px'
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={confirmActivate}
              variant="contained"
              disabled={isActivating}
              startIcon={isActivating ? <CircularProgress size={16} sx={{ color: 'white' }} /> : null}
              sx={{ 
                backgroundColor: '#4CAF50',
                textTransform: 'none',
                fontWeight: 600,
                minWidth: '100px',
                '&:hover': {
                  backgroundColor: '#45a049',
                },
                '&:disabled': {
                  backgroundColor: '#ccc',
                  color: '#666'
                }
              }}
            >
              {isActivating ? 'Activating...' : 'Activate'}
            </Button>
          </DialogActions>
        </Dialog>

      </Box>
    </Box>
  );
};

export default UserList;